/**
 * ComposeBox - Modern Unified Compose Area
 * 
 * A clean, Copilot-inspired compose area that integrates:
 * - Text input with auto-resize
 * - Tools & Agent mode toggle (inline)
 * - Context attachments
 * - Quick actions
 * - Voice input
 * 
 * Design principles:
 * - Single cohesive container
 * - Progressive disclosure
 * - Mobile-first responsive
 */

import React, { memo, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useAtom, useSetAtom, useAtomValue } from 'jotai';
import {
    Button,
    Tooltip,
    BiSend,
    BiX,
    BiRefresh,
    BiText,
    BiImageAlt,
    BiTrash,
    BsFillMicFill,
    BsMicMuteFill,
    BsRobot,
    BsChatDots,
    BsTools,
    BsGear,
    BsCheck2,
    BsPlus,
    BsPaperclip,
    AiOutlineLoading,
    IoMdClose,
    toast,
} from '@acme/ui';
import { toolMetadata, type ToolName } from '@acme/util';
import { composeContextImgsAtom, composeContextAtom } from '../store/util';
import {
    modelPopupAtom,
    activeAgentModeAtom,
    toolsActiveAtom,
    globalToolsConfigAtom,
    globalAgentConfigAtom,
    AGENT_PRESETS,
} from '../store/instruction';
import { chatQuickOptionsAtom } from '../store/setting';
import { upgradeModalAtom } from '../store/user';
import { useUserSubscription } from '../hooks';
import type { AgentMode } from '@acme/util';

// =============================================================================
// TYPES
// =============================================================================

interface ComposeBoxProps {
    onSend: () => void;
    onRegenerate?: () => void;
    onCancelResponse: () => void;
    isLastMessageLoading?: boolean;
    isLoading?: boolean;
    isAllowImage?: boolean;
    type?: 'chat' | 'note';
    /** Voice dictation state, owned by ComposeMessage. */
    isRecording?: boolean;
    /** Omit to hide the mic button entirely. */
    onToggleVoice?: () => void;
}

// =============================================================================
// MAIN COMPONENT
// =============================================================================

export const ComposeBox = memo(({
    onSend,
    onRegenerate,
    onCancelResponse,
    isLastMessageLoading,
    isLoading,
    isAllowImage = false,
    type = 'chat',
    isRecording = false,
    onToggleVoice,
}: ComposeBoxProps) => {
    const [inputValue, setInputValue] = useAtom(composeContextAtom);
    const [composeImgs, setComposeImgs] = useAtom(composeContextImgsAtom);
    const [agentMode, setAgentMode] = useAtom(activeAgentModeAtom);
    const [toolsActive, setToolsActive] = useAtom(toolsActiveAtom);
    const toolsConfig = useAtomValue(globalToolsConfigAtom);
    const agentConfig = useAtomValue(globalAgentConfigAtom);
    const setModelPopup = useSetAtom(modelPopupAtom);
    const setUpgradeModal = useSetAtom(upgradeModalAtom);
    const inputRef = useRef<HTMLTextAreaElement>(null);
    const [isFocused, setIsFocused] = useState(false);
    const { isAllowAgents, isAllowTools } = useUserSubscription();

    const hasContent = inputValue.length > 0;
    const hasImages = composeImgs.filter(img => img.url).length > 0;
    const imageCount = composeImgs.filter(img => img.url).length;
    const enabledToolsCount = toolsConfig.enabledTools.length;
    const currentPreset = AGENT_PRESETS[agentConfig.preset];
    const isAgent = agentMode === 'agent';

    // Auto-resize textarea
    useEffect(() => {
        if (inputRef.current) {
            inputRef.current.style.height = 'auto';
            const scrollHeight = Math.min(inputRef.current.scrollHeight, 200);
            inputRef.current.style.height = `${scrollHeight}px`;
        }
    }, [inputValue]);

    const handleKeyDown = useCallback((e: React.KeyboardEvent<HTMLTextAreaElement>) => {
        // Enter sends message, Shift+Enter adds new line
        if (e.key === 'Enter' && !e.shiftKey) {
            e.preventDefault();
            if (hasContent && !isLastMessageLoading) {
                onSend();
            }
        }
    }, [hasContent, isLastMessageLoading, onSend]);

    const handleSend = useCallback(() => {
        if (!hasContent || isLastMessageLoading) return;
        onSend();
    }, [hasContent, isLastMessageLoading, onSend]);

    const toggleAgentMode = useCallback(() => {
        if (!isAllowAgents.allow) {
            toast.error(isAllowAgents.message);
            setUpgradeModal({ isOpen: true, lastOpen: new Date() });
            return;
        }
        setAgentMode(prev => prev === 'chat' ? 'agent' : 'chat');
    }, [isAllowAgents.allow, isAllowAgents.message, setAgentMode, setUpgradeModal]);

    const toggleTools = useCallback(() => {
        if (!isAllowTools.allow) {
            toast.error(isAllowTools.message);
            setUpgradeModal({ isOpen: true, lastOpen: new Date() });
            return;
        }
        setToolsActive(prev => !prev);
    }, [isAllowTools.allow, isAllowTools.message, setToolsActive, setUpgradeModal]);

    const openToolsConfig = useCallback(() => {
        setModelPopup({ open: true, type: 'tools-config' });
    }, [setModelPopup]);

    const openAgentConfig = useCallback(() => {
        setModelPopup({ open: true, type: 'agent-config' });
    }, [setModelPopup]);

    const openAttachments = useCallback(() => {
        setModelPopup({ open: true, type: 'attach-file' });
    }, [setModelPopup]);

    const openPrompts = useCallback(() => {
        setModelPopup({ open: true, type: 'prompt' });
    }, [setModelPopup]);

    const clearInput = useCallback(() => {
        setInputValue('');
        setComposeImgs([]);
    }, [setInputValue, setComposeImgs]);

    const removeImage = useCallback((index: number) => {
        setComposeImgs(prev => prev.filter((_, i) => i !== index));
    }, [setComposeImgs]);

    return (
        <div className="w-full max-w-3xl mx-auto px-3 pb-4">
            {/* Main compose container.
                Uses a ring rather than a 2px border so focus doesn't shift layout,
                and a translucent backdrop so long messages scrolling underneath
                stay readable behind the composer. */}
            <div className={`
                relative flex flex-col
                bg-white/95 dark:bg-neutral-900/95 backdrop-blur-xl
                rounded-3xl overflow-hidden
                transition-shadow duration-200
                ring-1 ${isFocused
                    ? 'ring-2 ring-blue-500/70 shadow-xl shadow-blue-500/10'
                    : 'ring-neutral-200/80 dark:ring-neutral-700/80 shadow-lg shadow-black/5'
                }
            `}>
                {/* Top bar - Mode & Tools */}
                {type === 'chat' && (
                    <div className="flex items-center justify-between px-3 py-2 border-b border-gray-100 dark:border-neutral-700 bg-gray-50/50 dark:bg-neutral-800/50">
                        <div className="flex items-center gap-2">
                            {/* Mode Toggle */}
                            <ModeToggleButton
                                isAgent={isAgent}
                                onToggle={toggleAgentMode}
                                onConfigure={openAgentConfig}
                                preset={currentPreset}
                            />

                            <div className="w-px h-5 bg-gray-200 dark:bg-neutral-600" />

                            {/* Tools Toggle */}
                            <ToolsToggleButton
                                active={toolsActive}
                                count={enabledToolsCount}
                                onToggle={toggleTools}
                                onConfigure={openToolsConfig}
                            />
                        </div>

                        {/* Right side - Quick tool pills when expanded */}
                        {toolsActive && (
                            <div className="hidden md:flex items-center gap-1">
                                <QuickToolChips
                                    enabledTools={toolsConfig.enabledTools}
                                    maxVisible={3}
                                />
                            </div>
                        )}
                    </div>
                )}

                {/* Image previews (if any) */}
                {hasImages && (
                    <div className="flex items-center gap-2 px-3 py-2 border-b border-gray-100 dark:border-neutral-700 overflow-x-auto">
                        {composeImgs.filter(img => img.url).map((img, index) => (
                            <div key={index} className="relative group flex-shrink-0">
                                <img
                                    src={img.url}
                                    alt={`Attachment ${index + 1}`}
                                    className="w-14 h-14 object-cover rounded-lg border border-gray-200 dark:border-neutral-600"
                                />
                                <button
                                    onClick={() => removeImage(index)}
                                    className="absolute -top-1.5 -right-1.5 w-5 h-5 bg-red-500 text-white rounded-full flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity"
                                >
                                    <IoMdClose className="text-xs" />
                                </button>
                            </div>
                        ))}
                        <button
                            onClick={openAttachments}
                            className="w-14 h-14 flex-shrink-0 rounded-lg border-2 border-dashed border-gray-300 dark:border-neutral-600 flex items-center justify-center text-gray-400 hover:text-gray-600 hover:border-gray-400 dark:hover:text-gray-300 dark:hover:border-neutral-500 transition-colors"
                        >
                            <BsPlus className="text-2xl" />
                        </button>
                    </div>
                )}

                {/* Text input area */}
                <div className="relative">
                    <textarea
                        ref={inputRef}
                        value={inputValue}
                        onChange={(e) => setInputValue(e.target.value)}
                        onKeyDown={handleKeyDown}
                        onFocus={() => setIsFocused(true)}
                        onBlur={() => setIsFocused(false)}
                        placeholder={
                            isAgent
                                ? `Ask ${currentPreset.name} to help you...`
                                : "Type a message..."
                        }
                        rows={1}
                        className="
                            w-full px-4 py-3 pr-24
                            bg-transparent
                            text-gray-900 dark:text-white
                            placeholder:text-gray-400 dark:placeholder:text-gray-500
                            resize-none
                            focus:outline-none
                            text-base leading-relaxed
                            max-h-[200px] overflow-y-auto
                        "
                    />

                    {/* Send button - positioned inside input */}
                    <div className="absolute right-2 bottom-2 flex items-center gap-1">
                        {isLastMessageLoading ? (
                            <button
                                onClick={onCancelResponse}
                                className="flex items-center gap-1.5 px-3 py-1.5 bg-red-500 hover:bg-red-600 text-white text-sm font-medium rounded-lg transition-colors"
                            >
                                <BiX className="text-base" />
                                <span className="hidden sm:inline">Stop</span>
                            </button>
                        ) : (
                            <button
                                onClick={handleSend}
                                disabled={!hasContent}
                                className={`
                                    flex items-center justify-center
                                    w-9 h-9 rounded-lg
                                    transition-all duration-200
                                    ${hasContent
                                        ? 'bg-blue-500 hover:bg-blue-600 text-white shadow-md'
                                        : 'bg-gray-100 dark:bg-neutral-700 text-gray-400 dark:text-gray-500 cursor-not-allowed'
                                    }
                                `}
                            >
                                <BiSend className="text-lg" />
                            </button>
                        )}
                    </div>
                </div>

                {/* Bottom bar - Actions */}
                <div className="flex items-center justify-between px-3 py-2 border-t border-gray-100 dark:border-neutral-700 bg-gray-50/30 dark:bg-neutral-800/30">
                    <div className="flex items-center gap-1">
                        {/* Voice dictation. Lives here in the action row rather than
                            as a floating overlay button, which used to sit on top of
                            the composer and cover the bottom bar. */}
                        {onToggleVoice && (
                            <ActionButton
                                icon={isRecording
                                    ? <BsMicMuteFill className="text-base" />
                                    : <BsFillMicFill className="text-base" />}
                                tooltip={isRecording ? 'Stop recording' : 'Voice input'}
                                onClick={onToggleVoice}
                                active={isRecording}
                                variant={isRecording ? 'recording' : 'default'}
                            />
                        )}

                        {/* Attachment button */}
                        {isAllowImage && (
                            <ActionButton
                                icon={<BsPaperclip className="text-base" />}
                                label={hasImages ? `${imageCount}` : undefined}
                                tooltip="Attach files"
                                onClick={openAttachments}
                                active={hasImages}
                            />
                        )}

                        {/* Prompts button */}
                        <ActionButton
                            icon={<BiText className="text-base" />}
                            tooltip="Prompts library"
                            onClick={openPrompts}
                        />

                        {/* Regenerate button */}
                        {onRegenerate && !isLastMessageLoading && (
                            <ActionButton
                                icon={<BiRefresh className="text-base" />}
                                tooltip="Regenerate last response"
                                onClick={onRegenerate}
                            />
                        )}

                        {/* Clear button (when has content) */}
                        {(hasContent || hasImages) && (
                            <ActionButton
                                icon={<BiTrash className="text-sm" />}
                                tooltip="Clear all"
                                onClick={clearInput}
                                variant="danger"
                            />
                        )}
                    </div>

                    {/* Right side - hints */}
                    <div className="hidden sm:flex items-center gap-2 text-xs text-gray-400 dark:text-gray-500">
                        {isAgent && (
                            <span className="flex items-center gap-1 px-2 py-0.5 bg-purple-100 dark:bg-purple-900/30 text-purple-600 dark:text-purple-400 rounded">
                                <BsRobot className="text-xs" />
                                Agent mode
                            </span>
                        )}
                        {toolsActive && enabledToolsCount > 0 && (
                            <span className="flex items-center gap-1 px-2 py-0.5 bg-blue-100 dark:bg-blue-900/30 text-blue-600 dark:text-blue-400 rounded">
                                <BsTools className="text-xs" />
                                {enabledToolsCount} tools
                            </span>
                        )}
                        <span>⏎ Enter to send</span>
                    </div>
                </div>
            </div>
        </div>
    );
});

ComposeBox.displayName = 'ComposeBox';

// =============================================================================
// SUB-COMPONENTS
// =============================================================================

interface ModeToggleButtonProps {
    isAgent: boolean;
    onToggle: () => void;
    onConfigure: () => void;
    preset: { name: string; icon: string };
}

const ModeToggleButton = memo(({ isAgent, onToggle, onConfigure, preset }: ModeToggleButtonProps) => {
    return (
        <div className="flex items-center">
            <Tooltip message={isAgent ? 'Switch to Chat mode' : 'Switch to Agent mode'} position="bottom">
                <button
                    onClick={onToggle}
                    className={`
                        flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg text-xs font-medium
                        transition-all duration-200
                        ${isAgent
                            ? 'bg-purple-500 text-white shadow-md'
                            : 'bg-gray-100 dark:bg-neutral-700 text-gray-600 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-neutral-600'
                        }
                    `}
                >
                    {isAgent ? (
                        <>
                            <BsRobot className="text-sm" />
                            <span>Agent</span>
                            <span className="text-[10px] opacity-80">{preset.icon}</span>
                        </>
                    ) : (
                        <>
                            <BsChatDots className="text-sm" />
                            <span>Chat</span>
                        </>
                    )}
                </button>
            </Tooltip>
            {isAgent && (
                <Tooltip message="Configure agent" position="bottom">
                    <button
                        onClick={onConfigure}
                        className="ml-1 p-1.5 text-purple-400 hover:text-purple-300 hover:bg-purple-500/20 rounded transition-colors"
                    >
                        <BsGear className="text-xs" />
                    </button>
                </Tooltip>
            )}
        </div>
    );
});

ModeToggleButton.displayName = 'ModeToggleButton';

interface ToolsToggleButtonProps {
    active: boolean;
    count: number;
    onToggle: () => void;
    onConfigure: () => void;
}

const ToolsToggleButton = memo(({ active, count, onToggle, onConfigure }: ToolsToggleButtonProps) => {
    return (
        <div className="flex items-center">
            <Tooltip message={active ? 'Disable tools' : 'Enable AI tools'} position="bottom">
                <button
                    onClick={onToggle}
                    className={`
                        flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg text-xs font-medium
                        transition-all duration-200
                        ${active
                            ? 'bg-blue-500 text-white shadow-md'
                            : 'bg-gray-100 dark:bg-neutral-700 text-gray-600 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-neutral-600'
                        }
                    `}
                >
                    <BsTools className="text-sm" />
                    <span>Tools</span>
                    {active && count > 0 && (
                        <span className="flex items-center justify-center min-w-[18px] h-[18px] text-[10px] bg-white/20 rounded-full px-1">
                            {count}
                        </span>
                    )}
                </button>
            </Tooltip>
            <Tooltip message="Configure tools" position="bottom">
                <button
                    onClick={onConfigure}
                    className={`
                        ml-1 p-1.5 rounded transition-colors
                        ${active
                            ? 'text-blue-400 hover:text-blue-300 hover:bg-blue-500/20'
                            : 'text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 hover:bg-gray-100 dark:hover:bg-neutral-700'
                        }
                    `}
                >
                    <BsGear className="text-xs" />
                </button>
            </Tooltip>
        </div>
    );
});

ToolsToggleButton.displayName = 'ToolsToggleButton';

interface QuickToolChipsProps {
    enabledTools: string[];
    maxVisible: number;
}

const QuickToolChips = memo(({ enabledTools, maxVisible }: QuickToolChipsProps) => {
    const visibleTools = enabledTools.slice(0, maxVisible);
    const remainingCount = enabledTools.length - maxVisible;

    return (
        <div className="flex items-center gap-1">
            {visibleTools.map(toolName => {
                const meta = toolMetadata[toolName as ToolName];
                if (!meta) return null;
                return (
                    <span
                        key={toolName}
                        className="flex items-center gap-1 px-2 py-0.5 text-[10px] bg-blue-100 dark:bg-blue-900/40 text-blue-600 dark:text-blue-400 rounded-full"
                    >
                        <span>{meta.icon}</span>
                        <span className="hidden lg:inline">{meta.name}</span>
                    </span>
                );
            })}
            {remainingCount > 0 && (
                <span className="px-2 py-0.5 text-[10px] bg-gray-100 dark:bg-neutral-700 text-gray-500 dark:text-gray-400 rounded-full">
                    +{remainingCount}
                </span>
            )}
        </div>
    );
});

QuickToolChips.displayName = 'QuickToolChips';

interface ActionButtonProps {
    icon: React.ReactNode;
    label?: string;
    tooltip: string;
    onClick: () => void;
    active?: boolean;
    variant?: 'default' | 'danger' | 'recording';
}

const ActionButton = memo(({ icon, label, tooltip, onClick, active, variant = 'default' }: ActionButtonProps) => {
    const baseClasses = "flex items-center gap-1 px-2 py-1.5 rounded-lg text-xs transition-colors";
    const variantClasses = {
        default: active
            ? 'bg-blue-100 dark:bg-blue-900/30 text-blue-600 dark:text-blue-400'
            : 'text-gray-500 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-neutral-700 hover:text-gray-700 dark:hover:text-gray-300',
        danger: 'text-gray-400 hover:bg-red-100 dark:hover:bg-red-900/30 hover:text-red-500',
        recording: 'bg-red-500 text-white animate-pulse',
    };

    return (
        <Tooltip message={tooltip} position="top">
            <button
                onClick={onClick}
                className={`${baseClasses} ${variantClasses[variant]}`}
            >
                {icon}
                {label && <span className="font-medium">{label}</span>}
            </button>
        </Tooltip>
    );
});

ActionButton.displayName = 'ActionButton';

export default ComposeBox;
